import { useCallback, useEffect, useMemo, useState, type ComponentProps } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { DataTable, LoadingState, type DataTableColumn } from '../components/common';
import { loadMyManagerProfile } from '../services/managerService';
import { qnaService, type QnaDetail, type QnaListItem, type QnaState, type QnaType } from '../services/qnaService';

const qnaTypeLabels: Record<QnaType, string> = {
  ACCOUNT: '계정',
  RECORD: '기록',
  ETC: '기타',
};

const inquiryTableRowSx = {
  '&.MuiTableRow-hover:hover': {
    backgroundColor: '#f8fafc',
  },
};

function formatDate(value?: string) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return '요청 처리 중 오류가 발생했습니다.';
}

function getStatusLabel(state: QnaState) {
  return state === 'COMPLETED' ? '답변 완료' : '답변 대기';
}

function StatusChip({ state }: Readonly<{ state: QnaState }>) {
  const isAnswered = state === 'COMPLETED';

  return (
    <Chip
      label={getStatusLabel(state)}
      size="small"
      sx={{
        backgroundColor: isAnswered ? '#dcfce7' : '#fff7ed',
        color: isAnswered ? '#166534' : '#9a3412',
        fontSize: 11,
        fontWeight: 800,
      }}
    />
  );
}

function SecretChip({ isSecret }: Readonly<{ isSecret: boolean }>) {
  if (!isSecret) {
    return null;
  }

  return (
    <Chip
      label="비밀글"
      size="small"
      sx={{
        backgroundColor: '#eef2ff',
        color: '#3730a3',
        fontSize: 11,
        fontWeight: 800,
      }}
    />
  );
}

function DetailRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <Box sx={{ display: 'grid', gap: 0.5 }}>
      <Box sx={{ color: '#64748b', fontSize: 12, fontWeight: 800 }}>{label}</Box>
      <Box sx={{ color: '#111827', fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere' }}>
        {value || '-'}
      </Box>
    </Box>
  );
}

function getQnaId(rawId: string | null) {
  const id = Number(rawId);

  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }

  return id;
}

function QnaManagementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const qnaId = getQnaId(searchParams.get('id'));
  const isDetailMode = mode === 'detail';
  const isReplyMode = mode === 'reply';
  const [qnas, setQnas] = useState<QnaListItem[]>([]);
  const [activeQna, setActiveQna] = useState<QnaDetail | null>(null);
  const [answerDraft, setAnswerDraft] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [keyword, setKeyword] = useState('');
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');

  const columns = useMemo<DataTableColumn<QnaListItem>[]>(
    () => [
      {
        key: 'state',
        header: '답변 상태',
        sortable: true,
        width: 110,
        render: (qna) => <StatusChip state={qna.state} />,
      },
      {
        key: 'type',
        header: '분류',
        sortable: true,
        width: 120,
        render: (qna) => qnaTypeLabels[qna.type] ?? qna.type,
      },
      {
        key: 'title',
        header: '제목',
        sortable: true,
        width: 480,
        render: (qna) => (
          <Box sx={{ display: 'grid', gap: 0.5, maxWidth: 520 }}>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <Box sx={{ color: '#111827', fontWeight: 800 }}>{qna.title}</Box>
              <SecretChip isSecret={qna.secret} />
            </Box>
            <Box
              sx={{
                color: '#64748b',
                fontSize: 11,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {qna.content ?? '-'}
            </Box>
          </Box>
        ),
      },
      {
        key: 'writer',
        header: '작성자',
        sortable: true,
        width: 140,
      },
      {
        key: 'createdAt',
        header: '작성일',
        sortable: true,
        width: 180,
        render: (qna) => formatDate(qna.createdAt),
      },
      {
        key: 'answerWriterEmail',
        header: '답변자',
        sortable: true,
        width: 190,
        render: (qna) => qna.answerWriterEmail ?? '-',
      },
    ],
    []
  );

  const refreshList = useCallback(async (searchKeyword: string) => {
    setListLoading(true);
    setErrorMessage('');

    try {
      setQnas(await qnaService.list(searchKeyword));
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setListLoading(false);
    }
  }, []);

  const refreshDetail = async (id: number) => {
    setDetailLoading(true);
    setErrorMessage('');

    try {
      const detail = await qnaService.detail(id);
      setActiveQna(detail);
      setAnswerDraft(detail.answer?.content ?? '');
    } catch (error) {
      setActiveQna(null);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    void refreshList('');
  }, [refreshList]);

  const searchQnas = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    void refreshList(keyword);
  };

  useEffect(() => {
    void loadMyManagerProfile()
      .then((profile) => setManagerEmail(profile.email))
      .catch(() => setManagerEmail(''));
  }, []);

  useEffect(() => {
    if ((isDetailMode || isReplyMode) && qnaId !== null) {
      void refreshDetail(qnaId);
      return;
    }

    setActiveQna(null);
    setAnswerDraft('');
  }, [isDetailMode, isReplyMode, qnaId]);

  const openList = () => {
    setNoticeMessage('');
    navigate('/homepage/qna');
  };

  const openReply = (qna: QnaDetail) => {
    setAnswerDraft(qna.answer?.content ?? '');
    navigate(`/homepage/qna?mode=reply&id=${qna.id}`);
  };

  const openDeleteConfirm = () => {
    setDeleteErrorMessage('');
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (!deleting) {
      setDeleteConfirmOpen(false);
      setDeleteErrorMessage('');
    }
  };

  const handleDelete = async () => {
    if (!activeQna) {
      return;
    }

    setDeleting(true);
    setDeleteErrorMessage('');

    try {
      await qnaService.delete(activeQna.id);
      setDeleteConfirmOpen(false);
      setActiveQna(null);
      await refreshList(keyword);
      setNoticeMessage('QnA 문의를 삭제했습니다.');
      navigate('/homepage/qna');
    } catch (error) {
      setDeleteErrorMessage(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmitAnswer: NonNullable<ComponentProps<'form'>['onSubmit']> = async (event) => {
    event.preventDefault();

    const answerContent = answerDraft.trim();

    if (!activeQna || !answerContent) {
      setNoticeMessage('답변 내용을 입력해 주세요.');
      return;
    }

    if (!managerEmail) {
      setNoticeMessage('답변 작성자 이메일을 확인할 수 없습니다.');
      return;
    }

    setSaving(true);
    setNoticeMessage('');
    setErrorMessage('');

    try {
      const request = { writerEmail: managerEmail, answer: answerContent };

      if (activeQna.answer) {
        await qnaService.updateAnswer(activeQna.id, request);
      } else {
        await qnaService.createAnswer(activeQna.id, request);
      }

      await refreshList(keyword);
      await refreshDetail(activeQna.id);
      setNoticeMessage('답변이 저장되었습니다.');
      navigate(`/homepage/qna?mode=detail&id=${activeQna.id}`);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if ((isDetailMode || isReplyMode) && qnaId === null) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <p className="page-section">Homepage</p>
          <h1>QnA 관리</h1>
          <Alert severity="warning" sx={{ mt: 3 }}>
            QnA 정보를 찾을 수 없습니다.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Button onClick={openList} variant="outlined">
              목록으로
            </Button>
          </Box>
        </div>
      </section>
    );
  }

  if (isDetailMode) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <div>
              <p className="page-section">Homepage</p>
              <h1>QnA 상세</h1>
              <p className="page-description">러너가 남긴 문의 내용과 답변 상태를 확인합니다.</p>
            </div>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeQna && (
                <Button onClick={() => openReply(activeQna)} variant="contained">
                  {activeQna.answer ? '답글 수정' : '답글 작성'}
                </Button>
              )}
              {activeQna && (
                <Button color="error" onClick={openDeleteConfirm} variant="outlined">
                  삭제
                </Button>
              )}
              <Button onClick={openList} variant="outlined">
                목록으로
              </Button>
            </Box>
          </Box>

          {noticeMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNoticeMessage('')}>
              {noticeMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {detailLoading && <LoadingState message="데이터를 불러오는 중입니다." />}
          {!detailLoading && activeQna && (
            <Paper variant="outlined" sx={{ borderRadius: 1, boxShadow: 'none', p: 3 }}>
              <Box sx={{ borderBottom: '1px solid #e5e7eb', display: 'grid', gap: 2, pb: 3 }}>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
                  <StatusChip state={activeQna.state} />
                  <SecretChip isSecret={activeQna.secret} />
                </Box>
                <Box sx={{ color: '#111827', fontSize: 26, fontWeight: 900, lineHeight: 1.35 }}>
                  {activeQna.title}
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                  }}
                >
                  <DetailRow label="분류" value={qnaTypeLabels[activeQna.type] ?? activeQna.type} />
                  <DetailRow label="작성자" value={activeQna.writer} />
                  <DetailRow label="공개 여부" value={activeQna.secret ? '비밀글' : '공개글'} />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gap: 1.5, py: 3 }}>
                <Box sx={{ color: '#334155', fontSize: 13, fontWeight: 900 }}>문의 내용</Box>
                <Box
                  sx={{
                    color: '#1f2937',
                    fontSize: 14,
                    lineHeight: 1.8,
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {activeQna.content}
                </Box>
              </Box>

              <Box className="inquiry-answer-section">
                <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
                  <Box
                    className={`inquiry-answer-heading${activeQna.answer?.content ? ' has-answer' : ''}`}
                  >
                    <span aria-hidden="true" />
                    관리자 답변
                  </Box>
                  {activeQna.answer?.answeredAt && (
                    <Box sx={{ color: '#64748b', fontSize: 12 }}>
                      {activeQna.answer.writerEmail} · {formatDate(activeQna.answer.answeredAt)}
                    </Box>
                  )}
                </Box>
                <Box
                  className={activeQna.answer?.content ? 'inquiry-answer-content' : 'inquiry-answer-content is-empty'}
                  sx={{
                    color: activeQna.answer?.content ? '#1f2937' : '#94a3b8',
                    fontSize: 14,
                    lineHeight: 1.8,
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {activeQna.answer?.content || '등록된 답변이 없습니다.'}
                </Box>
              </Box>
            </Paper>
          )}
        </div>

        <Dialog fullWidth maxWidth="xs" onClose={closeDeleteConfirm} open={deleteConfirmOpen}>
          <DialogTitle sx={{ fontSize: 20, fontWeight: 800 }}>QnA 문의 삭제</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {deleteErrorMessage && <Alert severity="error">{deleteErrorMessage}</Alert>}
              <Box sx={{ color: '#374151', fontSize: 14, lineHeight: 1.7 }}>
                <strong>{activeQna?.title}</strong> 문의를 삭제할까요? 등록된 답변도 함께 삭제되며 복구할 수 없습니다.
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button disabled={deleting} onClick={closeDeleteConfirm} variant="outlined">
              취소
            </Button>
            <Button color="error" disabled={deleting} onClick={handleDelete} variant="contained">
              {deleting ? '삭제 중...' : '삭제'}
            </Button>
          </DialogActions>
        </Dialog>
      </section>
    );
  }

  if (isReplyMode) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <div>
              <p className="page-section">Homepage</p>
              <h1>{activeQna?.answer ? 'QnA 답글 수정' : 'QnA 답글 작성'}</h1>
              <p className="page-description">문의 내용을 확인하고 러너에게 전달할 답변을 작성합니다.</p>
            </div>
            <Button
              disabled={!activeQna}
              onClick={() => activeQna && navigate(`/homepage/qna?mode=detail&id=${activeQna.id}`)}
              variant="outlined"
            >
              상세로
            </Button>
          </Box>

          {noticeMessage && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNoticeMessage('')}>
              {noticeMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {detailLoading && <LoadingState message="데이터를 불러오는 중입니다." />}
          {!detailLoading && activeQna && (
            <Paper component="form" onSubmit={handleSubmitAnswer} variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gap: 2.5 }}>
                <Box sx={{ borderBottom: '1px solid #e5e7eb', display: 'grid', gap: 1, pb: 2 }}>
                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                    <StatusChip state={activeQna.state} />
                    <SecretChip isSecret={activeQna.secret} />
                    <Box sx={{ color: '#64748b', fontSize: 13 }}>
                      {qnaTypeLabels[activeQna.type] ?? activeQna.type}
                    </Box>
                  </Box>
                  <Box sx={{ color: '#111827', fontSize: 20, fontWeight: 900 }}>
                    {activeQna.title}
                  </Box>
                  <Box
                    sx={{
                      color: '#475569',
                      fontSize: 14,
                      lineHeight: 1.8,
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {activeQna.content}
                  </Box>
                </Box>

                <TextField
                  disabled={saving}
                  label="답변 내용"
                  minRows={10}
                  multiline
                  onChange={(event) => setAnswerDraft(event.target.value)}
                  placeholder="러너에게 전달할 답변을 입력해 주세요."
                  required
                  value={answerDraft}
                />

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    disabled={saving}
                    onClick={() => navigate(`/homepage/qna?mode=detail&id=${activeQna.id}`)}
                    variant="outlined"
                  >
                    취소
                  </Button>
                  <Button disabled={saving} type="submit" variant="contained">
                    {saving ? '저장 중' : '답변 저장'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}
        </div>
      </section>
    );
  }

  const waitingCount = qnas.filter((qna) => qna.state === 'WAITING').length;

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <div className="page-title-block">
          <p className="page-section">Homepage</p>
          <h1>QnA 관리</h1>
          <p className="page-description">
            홈페이지 러너 QnA 문의를 확인하고 답변 상태를 관리합니다.
          </p>
        </div>

        {noticeMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNoticeMessage('')}>
            {noticeMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={searchQnas}
          sx={{ alignItems: 'flex-end', display: 'flex', gap: 1.5, justifyContent: 'space-between', mb: 2 }}
        >
          <Box sx={{ alignItems: 'flex-end', display: 'flex', gap: 1, maxWidth: 460, width: '100%' }}>
            <TextField
              label="QnA 검색"
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="제목 또는 작성자 이름"
              size="small"
              sx={{ flex: 1 }}
              value={keyword}
            />
            <Button disabled={listLoading} type="submit" variant="contained">
              검색
            </Button>
          </Box>
          <Box sx={{ color: '#64748b', fontSize: 13, fontWeight: 700 }}>
            전체 {qnas.length}건 · 답변 대기 {waitingCount}건
          </Box>
        </Box>

        {listLoading ? (
          <LoadingState message="데이터를 불러오는 중입니다." />
        ) : (
          <DataTable
            columns={columns}
            emptyMessage="접수된 QnA가 없습니다."
            onRowClick={(qna) => navigate(`/homepage/qna?mode=detail&id=${qna.id}`)}
            rowKey="id"
            rowSx={() => inquiryTableRowSx}
            rows={qnas}
          />
        )}
      </div>
    </section>
  );
}

export default QnaManagementPage;
